import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Card, CardContent, Dialog, DialogContent, Grid } from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { groupBy, isNil, isUndefined, keys, uniqBy } from 'lodash'
import DtoSearchIndices from 'quality/dto/DtoSearchIndices'
import QualityChart from 'components/echart/QualityChart'
import ReactECharts from 'echarts-for-react'
import echarts from 'echarts/lib/echarts'
import useListIndexed from 'utils/customHook/useListIndexed'
import { INDICES_RANGE } from 'quality/constants/HydrobioConstant'
import QualityAction from 'quality/actions/QualityAction'
import { NoDataPanel } from 'components/card/NoDataCard'
import ProgressCard from 'components/card/ProgressCard'
import { getDate } from 'utils/DateUtil'
import { exportExcelIcon, exportPictureIcon } from 'components/echart/EChartUtils'
import { exportFile } from 'utils/ExportDataUtil'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { blackColor, secondaryBlue } from 'utils/constants/ColorTheme'

const getStationName = (station) => {
    if (station?.code) {
        return `[${station?.code}] ${station?.name || ''}`
    }
    return `${station?.name || ''}`
}

const IndiceGraph = ({
    indices = [],

    graphOptions = {},
    componentHeight = 600,
    withToolLegend = false,
    exportName,

    dataZoomPosition = { bottom: 40 },
    legendPosition = { bottom: 10, right: 20, top: undefined },
    toolboxPosition = { top: 5 },
}) => {
    const {
        qualitometers,
        parameters,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        parameters: store.ParameterReducer.parameters,
    }), shallowEqual)

    const {
        // regroupAxis = false,
        graphicTitle,

        minY = 0,
        maxY,
        xAxisSpace,
        maxXAxisSpace,
        showXSplitLines,
        showYSplitLines,

        // stationsOptions = [],
    } = graphOptions

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const parametersCode = useMemo(() => uniqBy(indices, 'parameter').map(a => a.parameter), [indices])

    const indicesGroupByStation = groupBy(indices, 'stationId')
    const data = keys(indicesGroupByStation).map(key => {
        const parseStationId = parseInt(key)
        const qualitometer = qualitometers.find(q => q.id === parseStationId)
        return {
            name: getStationName(qualitometer),
            dataList: indicesGroupByStation[key].map(i => ({
                date: i.sampleDate,
                value: i.result,
            })),
            type: 'bar',
        }
    })

    const parametersObj = parametersCode.map(code => parameters.find(p => p.code === code)).filter(u => !!u)
    const chartTitle = isUndefined(graphicTitle) ? `${parametersObj.map(p => showSandreCode ? p.displayLabel : p.displayName).join(', ')}` : graphicTitle

    return (
        <QualityChart
            title={chartTitle}
            exportName={exportName ?? `${i18n.follow} - ${parametersObj.map(p => p.displayName).join(', ')}`}
            grids={[{
                height: 'auto',
                bottom: 120,
                top: 10,
            }]}
            xAxis={[{
                splitLine: { show: showXSplitLines },
            }]}
            yAxis={[{
                name: i18n.indiceValue,
                min: minY,
                max: maxY,
                splitLine: { show: showYSplitLines },
            }]}
            series={data}

            headerHeight={0}
            footerHeight={120}
            componentHeight={componentHeight}

            xAxisSpace={xAxisSpace}
            maxXAxisSpace={maxXAxisSpace}

            dataZoomPosition={dataZoomPosition}
            legendPosition={legendPosition}
            toolboxPosition={toolboxPosition}

            // withToolTypeLine
            // withToolTypeBar
            withToolLegend={withToolLegend}
            withDataZoom
        />
    )
}

IndiceGraph.propTypes = {
    indices: PropTypes.arrayOf(PropTypes.instanceOf(DtoSearchIndices)),
    graphOptions: PropTypes.shape({
        regroupAxis: PropTypes.bool,
        graphicTitle: PropTypes.string,

        minY: PropTypes.number,
        maxY: PropTypes.number,
        xAxisSpace: PropTypes.oneOf(['year', 'month', 'month', 'AUTO']),
        maxXAxisSpace: PropTypes.oneOf(['year', 'month', 'week', 'AUTO']),
        showXSplitLines: PropTypes.bool,
        showYSplitLines: PropTypes.bool,

        stationsOptions: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            color: PropTypes.string,
        })),

    }),
    graphHeight: PropTypes.number,
    componentHeight: PropTypes.number,
    withToolLegend: PropTypes.bool,
    exportName: PropTypes.string,
    dataZoomPosition: PropTypes.shape({}),
    legendPosition: PropTypes.shape({}),
    gridPosition: PropTypes.shape({}),
    toolboxPosition: PropTypes.shape({}),

}

const SuperIndiceGraphModal = ({
    code,
    isGraphOpen = false,
    indiceCode,
    closeGraph = () => {},
}) => {
    const [indices, setIndices] = useState([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (isNil(indiceCode)) return

        setLoading(true)
        QualityAction.promiseSearchIndices({ stations: [code], parameters: [indiceCode] }).then(json => {
            setIndices(json.map(el => new DtoSearchIndices(el)))
            setLoading(false)
        })
    }, [code, indiceCode])

    return (
        <IndiceGraphModal
            isOpen={isGraphOpen}
            indices={indices}
            closeGraph={closeGraph}
            indiceCode={indiceCode}
            loading={loading}
        />
    )
}

SuperIndiceGraphModal.propTypes = {
    code: PropTypes.string,
    isGraphOpen: PropTypes.bool,
    indiceCode: PropTypes.string,
    closeGraph: PropTypes.func,
}

const IndiceGraphModal = ({
    isOpen = false,
    closeGraph = () => { },
    indices = [],
    indiceCode = '',
    loading = false,
}) => {
    const {
        parameters,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const parameterCode = indices[0]?.parameter

    const parameterObj = parameters.find(p => p.code === parameterCode || p.code === indiceCode)
    const dialogTitle = `${i18n.followUp} ${parameterObj?.name || ''} ${(showSandreCode && parameterCode) ? `[${parameterCode}]` : ''}`
    return (
        <Dialog maxWidth='lg' fullWidth open={isOpen}>
            <DefaultDialogTitle title={dialogTitle} onClose={closeGraph}/>
            <DialogContent>
                <Grid container columnSpacing={2}>
                    <Grid size={12}>
                        { loading && <ProgressCard indeterminate /> || (
                            <Card>
                                <CardContent>
                                    { !!indices.length && (

                                        <IndiceGraph
                                            indices={indices}
                                        />
                                    ) || <NoDataPanel />}
                                </CardContent>
                            </Card>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    )
}

IndiceGraphModal.propTypes = {
    isOpen: PropTypes.bool,
    closeGraph: PropTypes.func,
    indices: PropTypes.arrayOf(PropTypes.instanceOf(DtoSearchIndices)),
    indiceCode: PropTypes.string,
    loading: PropTypes.bool,
}

const IndiceRadarGraph = ({
    indices = [],
    withTitle = false,
    height = 300,
}) => {
    const {
        parameters,
    } = useSelector(store =>({
        parameters: store.ParameterReducer.parameters,
    }), shallowEqual)

    const indexedParameters = useListIndexed(parameters, 'code')

    let echartRef = useRef(undefined)

    const formattedIndicators = indices.map(i => {
        const indiceRange = INDICES_RANGE.find(ind => ind.code === `${i.parameter}`)
        return {
            name: indexedParameters[i.parameter]?.displayName ?? `${i.parameter}`,
            max: indiceRange?.max ?? 20,
            min: indiceRange?.min ?? 0,
        }
    })

    const onExport = () => {
        const exportData = indices.map(p => ({
            indice: { value: indexedParameters[p.parameter]?.name ?? `${p.parameter}` },
            value: { value: p.result, format: '0.00000', cellType: 'number' },
            date: { value: getDate(p.sampleDate), format: 'dd/MM/yyyy', cellType: 'date' },
        }))
        exportFile({
            data: exportData.length ? [
                {
                    ...exportData[0],
                    headers: ['indice', 'value', 'date'],
                },
                ...exportData.slice(1),
            ] : [],
            exportType: 'xlsx',
            titleFile: i18n.hydrobioIndices,
        }, true)
    }

    const getToolbox = show => ({
        show,
        right: 70,
        showTitle: false,
        itemSize: 18,
        tooltip: {
            show: true,
            position: 'bottom',
        },
        feature: {
            saveAsImage: {
                title: i18n.pictureExport,
                icon: exportPictureIcon,
                name: i18n.hydrobioIndices,
            },
            myToolExport: {
                title: i18n.excelExport,
                icon: exportExcelIcon,
                onclick: onExport,
            },
        },
    })

    const options = {
        title: {
            show: withTitle,
            text: i18n.hydrobioIndices,
            x: 'center',
            top: '0',
        },
        tooltip: {
            confine: false,
            appendToBody: true,
            textStyle: {
                fontSize: 12,
            },
            formatter: params => {
                return params.data.value.map((value, index) => {
                    const code = params.data.indices[index]?.parameter
                    const indicatorName = indexedParameters[code]?.displayName ?? `${code}`
                    const indicatorDate = params.data.indices[index]?.sampleDate
                    return `<strong>${indicatorName}</strong>${indicatorDate ? ` (${getDate(indicatorDate)})` : ''}: <strong>${value}</strong>`
                }).join('<br/>')
            },
        },
        radar: {
            shape: 'circle',
            radius: '130',
            name: {
                textStyle: {
                    color: blackColor,
                    padding: [3, 5],
                },
            },
            indicator: formattedIndicators,
        },
        series: [{
            name: i18n.hydrobioIndices,
            type: 'radar',
            lineStyle: { color: secondaryBlue },
            areaStyle: { color: secondaryBlue, opacity: 0.5 },
            itemStyle: { color: secondaryBlue },
            data: [
                {
                    value: indices.map(i => i.result ?? ''),
                    indices,
                },
            ],
        }],
    }

    return (
        <div
            onMouseOver={() => {
                echartRef.current?.getEchartsInstance().setOption({
                    toolbox: getToolbox(true),
                })
            }}
            onMouseOut={() => {
                echartRef.current?.getEchartsInstance().setOption({
                    toolbox: getToolbox(false),
                })
            }}
        >
            <ReactECharts
                echarts={echarts}
                option={options}
                notMerge
                lazyUpdate
                ref={e => {
                    echartRef.current = e
                }}
                style={{ height }}
            />
        </div>
    )
}

IndiceRadarGraph.propTypes = {
    indices: PropTypes.arrayOf(PropTypes.shape({})),
    height: PropTypes.number,
    withTitle: PropTypes.bool,
}

export {
    IndiceGraphModal,
    IndiceGraph,
    IndiceRadarGraph,
    SuperIndiceGraphModal,
}